import { Button, Group, Stack } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useLocalStorage } from "@mantine/hooks";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useTranslation } from "react-i18next";

import { CurrencyInput, Dropdown } from "@/components/elements";
import { defaultCurrency } from "@/lib/currency";
import { deleteCarInputSchema } from "@/services/cars/delete-car";
import { Currency } from "@/types/currency";

import type { DeleteCarInput } from "@/services/cars/delete-car";
import type { FC } from "react";

export type DeleteCarFormProps = {
  initialValues?: Partial<DeleteCarInput>;
  labels?: {
    cancelButton?: string;
    submitButton?: string;
  };
  loading?: boolean;
  onCancel?: () => void;
  onSubmit: (data: DeleteCarInput) => void;
};

export const DeleteCarForm: FC<DeleteCarFormProps> = ({
  initialValues,
  labels,
  loading = false,
  onCancel,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const form = useForm<DeleteCarInput>({
    mode: "controlled",
    initialValues: {
      exit_price: 0,
      status: "sold",
      ...initialValues,
    },
    validate: zod4Resolver(deleteCarInputSchema),
    validateInputOnBlur: true,
  });

  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack gap="md" py="xs">
        <Dropdown
          key={form.key("status")}
          data={[
            { value: "sold", label: t("components.DeleteCar.statuses.sold") },
            { value: "traded", label: t("components.DeleteCar.statuses.traded") },
            { value: "totaled", label: t("components.DeleteCar.statuses.totaled") },
            { value: "other", label: t("components.DeleteCar.statuses.other") },
          ]}
          label="Status"
          allowDeselect={false}
          {...form.getInputProps("status")}
          withAsterisk
        />
        <CurrencyInput
          key={form.key("exit_price")}
          currencySymbol={currency.symbol}
          label="Exit price"
          value={(Number(form.values.exit_price) || 0) * (Number(currency.value) || 1)}
          onChange={(value) => {
            const adjustedValue = (Number(value) || 0) / (Number(currency.value) || 1);
            form.setFieldValue("exit_price", adjustedValue);
          }}
          withAsterisk
        />
        <Group justify="center" mt="lg">
          {onCancel && (
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={loading}
              aria-label={t("components.Form.cancelButton")}
            >
              {labels?.cancelButton || t("components.Form.cancelButton")}
            </Button>
          )}
          <Button
            type="submit"
            disabled={!form.isValid()}
            loading={loading}
            aria-label={t("components.DeleteCar.submitButton")}
          >
            {labels?.submitButton || t("components.DeleteCar.submitButton")}
          </Button>
        </Group>
      </Stack>
    </form>
  );
};
